<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card class="q-dialog-plugin shift-dialog">
      <q-form @submit.prevent="shiftTasks">
        <q-card-section class="q-pb-sm">
          <div class="text-h6">Сдвинуть выбранные задачи</div>
          <div class="text-caption text-grey-7 q-mt-xs">
            Выбрано задач: {{ tasks.length }}
          </div>
        </q-card-section>

        <q-card-section class="q-pt-sm">
          <q-input
            v-model.number="days"
            type="number"
            filled
            label="Количество дней"
            hint="Положительное число — вперед, отрицательное — назад"
            :rules="[daysRequiredRule, nonZeroRule]"
          />

          <div class="q-mt-md text-caption text-grey-8">Быстрый выбор</div>
          <div class="row q-col-gutter-sm q-mt-xs">
            <div v-for="preset in presets" :key="preset" class="col-auto">
              <q-btn
                outline
                no-caps
                color="primary"
                size="sm"
                :label="presetLabel(preset)"
                @click="days = preset"
              />
            </div>
          </div>

          <q-banner
            v-if="days !== null && !Number.isNaN(days) && days !== 0"
            class="bg-blue-1 text-blue-9 rounded-borders q-mt-md"
          >
            Сдвиг на {{ Math.abs(days) }} дн. {{ days > 0 ? 'вперед' : 'назад' }}
          </q-banner>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn :loading="loading" :disable="!canSubmit" no-caps label="Сдвинуть" type="submit" color="primary" />
          <q-btn :disable="loading" color="primary" label="Отмена" no-caps @click="onDialogCancel" />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue'
import { useDialogPluginComponent, useQuasar } from 'quasar'
import { useRouteParams } from '@vueuse/router'
import { useEventBus } from '@vueuse/core'
import { taskKey } from '../util/events'
import { useShiftPerformTasks } from '../composables/performTask'

const props = defineProps<{
  tasks: number[]
}>()

const { tasks } = toRefs(props)
const $q = useQuasar()
const projectId = useRouteParams('projectId', undefined, { transform: Number })
const taskEvent = useEventBus(taskKey)

const days = ref<number | null>(1)
const loading = ref(false)
const presets = [-7, -3, -1, 1, 3, 7]

const canSubmit = computed(() => days.value !== null && !Number.isNaN(days.value) && days.value !== 0)

const daysRequiredRule = (value: number | null) => value !== null && !Number.isNaN(value) || 'Введите количество дней'
const nonZeroRule = (value: number | null) => value !== 0 || 'Введите число, отличное от 0'

const { execute } = useShiftPerformTasks(projectId, tasks, computed(() => days.value ?? 0))

function presetLabel(value: number) {
  return value > 0 ? `+${value}` : `${value}`
}

async function shiftTasks() {
  if (!canSubmit.value) {
    return
  }

  loading.value = true
  try {
    await execute(true)
    taskEvent.emit('refresh')
    $q.notify({ type: 'positive', message: 'Задачи сдвинуты', position: 'top-right' })
    onDialogOK()
  } finally {
    loading.value = false
  }
}

defineEmits([
  ...useDialogPluginComponent.emits
])

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()
</script>

<style scoped>
.shift-dialog {
  width: min(560px, 95vw);
  max-width: 95vw;
}
</style>
